import {useCallback, useMemo, useRef, useState} from 'react';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
  VStack,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';
import {motion, LayoutGroup} from 'framer-motion';
import {TablerModalCloseButton as ModalCloseButton} from './TablerCloseButtons';
import {TablerChevronDown, TablerSearch, TablerX} from '../../icons';
import {SUPPORTED_LANGUAGES, COMMON_LANGUAGES} from '../../common/languages';
import FavoriteStar from './FavoriteStar';
import useLanguageFavorites from '../../hooks/useLanguageFavorites';
import {partitionByFavorite} from '../../common/languageFavoritesOrder';

const BASE_LANGUAGE_NAMES = Object.keys(SUPPORTED_LANGUAGES);

const MotionBox = motion(Box);

export default function LanguageDialectPicker({
  selectedLanguage,
  selectedDialect,
  onLanguageChange,
  onDialectChange,
  languageLabel = 'Preferred Language',
  dialectLabel = 'Dialect',
  isLanguageRequired = false,
  labelProps = {},
  size = 'md',
  borderRadius = 'lg',
  includeEnglish = false,
  // Overrides the empty-state trigger text ('Auto-detect' in compact mode,
  // 'Search languages...' otherwise).
  placeholder = '',
}: Record<string, any>) {
  const ALL_LANGUAGE_NAMES = useMemo(() => {
    if (!includeEnglish || BASE_LANGUAGE_NAMES.includes('English'))
      return BASE_LANGUAGE_NAMES;
    return ['English', ...BASE_LANGUAGE_NAMES];
  }, [includeEnglish]);
  const {isOpen, onOpen, onClose} = useDisclosure();
  const {favoriteSet, isFavorite, toggleFavorite} = useLanguageFavorites();
  const [searchQuery, setSearchQuery] = useState('');
  const searchRef = useRef<any>(null);

  const btnBg = useColorModeValue('white', 'gray.700');
  const btnBorder = useColorModeValue('gray.200', 'gray.600');
  const placeholderColor = useColorModeValue('gray.500', 'gray.400');
  const modalBg = useColorModeValue('white', 'gray.800');
  const hoverBg = useColorModeValue('gray.100', 'gray.700');
  const sectionColor = useColorModeValue('gray.600', 'gray.300');
  const selectedBg = useColorModeValue('gray.50', 'gray.900');
  const selectedBorder = useColorModeValue('gray.400', 'gray.500');
  const commonBadgeBg = useColorModeValue('gray.50', 'gray.900');
  const commonBadgeColor = useColorModeValue('gray.700', 'gray.200');

  const dialects = selectedLanguage
    ? SUPPORTED_LANGUAGES[selectedLanguage] || []
    : [];

  const filteredLanguages = useMemo(() => {
    if (!searchQuery.trim()) return ALL_LANGUAGE_NAMES;
    const q = searchQuery.trim().toLowerCase();
    return ALL_LANGUAGE_NAMES.filter(lang => lang.toLowerCase().includes(q));
  }, [ALL_LANGUAGE_NAMES, searchQuery]);

  const isCommonLang = useCallback(
    (l: any) =>
      COMMON_LANGUAGES.includes(l) || (includeEnglish && l === 'English'),
    [includeEnglish],
  );

  const commonFiltered = useMemo(
    () => filteredLanguages.filter(isCommonLang),
    [filteredLanguages, isCommonLang],
  );

  const otherFiltered = useMemo(
    () => filteredLanguages.filter(l => !isCommonLang(l)),
    [filteredLanguages, isCommonLang],
  );

  // Favorites float into their own group at the top; pull them out of the
  // Common / All lists so a starred language doesn't appear twice.
  const favoriteFiltered = useMemo(
    () => filteredLanguages.filter(l => favoriteSet.has(l)),
    [filteredLanguages, favoriteSet],
  );
  const commonFilteredNonFav = useMemo(
    () => commonFiltered.filter(l => !favoriteSet.has(l)),
    [commonFiltered, favoriteSet],
  );
  const otherFilteredNonFav = useMemo(
    () => otherFiltered.filter(l => !favoriteSet.has(l)),
    [otherFiltered, favoriteSet],
  );
  // While searching, the modal shows a flat result list — keep favorites first.
  const searchOrdered = useMemo(
    () => partitionByFavorite(filteredLanguages, l => l, favoriteSet),
    [filteredLanguages, favoriteSet],
  );

  const handleSelect = (lang: any) => {
    onLanguageChange(lang);
    setSearchQuery('');
    onClose();
  };

  const renderHeader = (label: string, key: string) => (
    <MotionBox key={key} layout>
      <Text
        fontSize="sm"
        fontWeight="normal"
        color={sectionColor}
        textTransform="uppercase"
        letterSpacing="wider"
        mb={2}
        mt={2}
        px={1}
      >
        {label}
      </Text>
    </MotionBox>
  );

  // The `layout` wrapper plus the shared single VStack parent are what let a
  // row slide up into the Favorites group (or back down) when its star is
  // toggled, rather than the list jumping.
  const renderRow = (lang: string) => (
    <MotionBox key={lang} layout>
      <LanguageRow
        language={lang}
        isSelected={selectedLanguage === lang}
        isCommon={isCommonLang(lang)}
        isFavorite={isFavorite(lang)}
        onToggleFavorite={() => toggleFavorite(lang)}
        hoverBg={hoverBg}
        selectedBg={selectedBg}
        selectedBorder={selectedBorder}
        commonBadgeBg={commonBadgeBg}
        commonBadgeColor={commonBadgeColor}
        onSelect={handleSelect}
      />
    </MotionBox>
  );

  const handleClear = (e: any) => {
    e.stopPropagation();
    e.preventDefault();
    onLanguageChange('');
  };

  const handleOpen = () => {
    setSearchQuery('');
    onOpen();
  };

  const compact = !languageLabel && !dialectLabel;

  return (
    <>
      <Flex
        w="100%"
        gap={compact ? 2 : 3}
        direction={compact ? 'column' : {base: 'column', md: 'row'}}
        align="flex-start"
      >
        <FormControl isRequired={isLanguageRequired}>
          {languageLabel && (
            <FormLabel fontSize="sm" fontWeight="normal" {...labelProps}>
              {languageLabel}
            </FormLabel>
          )}
          <HStack spacing={0}>
            <Button
              onClick={handleOpen}
              flex="1"
              justifyContent="space-between"
              fontWeight="normal"
              bg={btnBg}
              borderWidth="1px"
              borderColor={btnBorder}
              borderRadius={borderRadius}
              borderRightRadius={selectedLanguage && !compact ? 0 : undefined}
              size={size}
              minH="44px"
              _hover={{borderColor: 'gray.400'}}
              _focus={{
                borderColor: 'gray.400',
                boxShadow: '0 0 0 2px var(--chakra-colors-gray-400)',
              }}
              rightIcon={<TablerChevronDown />}
              aria-haspopup="dialog"
              aria-expanded={isOpen}
              aria-label={
                selectedLanguage
                  ? `Selected language: ${selectedLanguage}. Click to change.`
                  : 'Select a language'
              }
            >
              <Text
                isTruncated
                fontSize="md"
                color={selectedLanguage ? undefined : placeholderColor}
              >
                {selectedLanguage ||
                  placeholder ||
                  (compact ? 'Auto-detect' : 'Search languages...')}
              </Text>
            </Button>
            {selectedLanguage && !compact && (
              <IconButton
                icon={<TablerX />}
                aria-label="Clear language selection"
                onClick={handleClear}
                size={size}
                minH="44px"
                minW="44px"
                variant="outline"
                borderColor={btnBorder}
                borderLeft="none"
                borderRadius={borderRadius}
                borderLeftRadius={0}
                _hover={{bg: 'gray.100'}}
              />
            )}
          </HStack>
        </FormControl>

        {dialects.length > 0 && !compact && (
          <FormControl>
            {dialectLabel && (
              <FormLabel fontSize="sm" fontWeight="normal" {...labelProps}>
                {dialectLabel}
              </FormLabel>
            )}
            <Select
              value={selectedDialect}
              onChange={e => onDialectChange(e.target.value)}
              placeholder="Select dialect"
              borderRadius={borderRadius}
              size={size}
              h="44px"
              fontSize="sm"
              aria-label={`Select dialect for ${selectedLanguage}`}
            >
              {dialects.map(d => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </Select>
          </FormControl>
        )}
      </Flex>

      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size={{base: 'full', md: 'md'}}
        scrollBehavior="inside"
        initialFocusRef={searchRef}
      >
        <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(4px)" />
        <ModalContent
          bg={modalBg}
          borderRadius={{base: 0, md: '2xl'}}
          mx={{base: 0, md: 4}}
          my={{base: 0, md: 'auto'}}
          maxH={{base: '100vh', md: '70vh'}}
          pb={{base: 'env(safe-area-inset-bottom, 0px)', md: 0}}
          role="dialog"
          aria-label="Language selection"
        >
          <ModalHeader pb={2} fontSize="lg">
            Select Language
          </ModalHeader>
          <ModalCloseButton size="lg" />
          <ModalBody px={4} pb={4}>
            <VStack spacing={3} align="stretch">
              <InputGroup size="lg">
                <InputLeftElement pointerEvents="none">
                  <TablerSearch color="var(--chakra-colors-gray-500)" />
                </InputLeftElement>
                <Input
                  ref={searchRef}
                  placeholder="Search languages..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  borderRadius="xl"
                  fontSize="md"
                  autoComplete="off"
                  aria-label="Search languages"
                  aria-controls="language-list"
                />
              </InputGroup>

              {searchQuery && (
                <Text
                  fontSize="md"
                  color={sectionColor}
                  px={1}
                  role="status"
                  aria-live="polite"
                >
                  {filteredLanguages.length} result
                  {filteredLanguages.length !== 1 ? 's' : ''}
                </Text>
              )}

              <Box
                id="language-list"
                role="listbox"
                aria-label="Languages"
                overflowY="auto"
                flex="1"
                maxH={{base: 'calc(100vh - 200px)', md: '50vh'}}
                mx={-2}
                px={2}
                sx={{WebkitOverflowScrolling: 'touch'}}
              >
                <LayoutGroup>
                  <VStack spacing={1} align="stretch">
                    {searchQuery ? (
                      <>
                        {searchOrdered.favorites.length > 0 &&
                          renderHeader('Favorites', 'header-favorites')}
                        {searchOrdered.favorites.map(renderRow)}
                        {searchOrdered.others.map(renderRow)}
                      </>
                    ) : (
                      <>
                        {favoriteFiltered.length > 0 &&
                          renderHeader('Favorites', 'header-favorites')}
                        {favoriteFiltered.map(renderRow)}
                        {commonFilteredNonFav.length > 0 &&
                          renderHeader('Common Languages', 'header-common')}
                        {commonFilteredNonFav.map(renderRow)}
                        {otherFilteredNonFav.length > 0 &&
                          renderHeader('All Languages', 'header-all')}
                        {otherFilteredNonFav.map(renderRow)}
                      </>
                    )}
                  </VStack>
                </LayoutGroup>

                {searchQuery && filteredLanguages.length === 0 && (
                  <Text
                    color={sectionColor}
                    textAlign="center"
                    py={8}
                    fontSize="md"
                  >
                    No languages matching &ldquo;{searchQuery}&rdquo;
                  </Text>
                )}
              </Box>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}

function LanguageRow({
  language,
  isSelected,
  isCommon,
  isFavorite,
  onToggleFavorite,
  hoverBg,
  selectedBg,
  selectedBorder,
  commonBadgeBg,
  commonBadgeColor,
  onSelect,
}: Record<string, any>) {
  const dialectCount = SUPPORTED_LANGUAGES[language]?.length || 0;
  const dialectColor = useColorModeValue('gray.600', 'gray.300');

  return (
    <Button
      variant="ghost"
      justifyContent="space-between"
      onClick={() => onSelect(language)}
      borderRadius="lg"
      // Fill the stretched MotionBox wrapper — otherwise the button shrinks
      // to its content and space-between collapses ("Spanish8 dialects").
      w="100%"
      h="auto"
      minH="48px"
      py={2.5}
      px={3}
      fontWeight="normal"
      bg={isSelected ? selectedBg : 'transparent'}
      borderWidth={isSelected ? '1px' : '0'}
      borderColor={isSelected ? selectedBorder : 'transparent'}
      _hover={{bg: isSelected ? selectedBg : hoverBg}}
      _focus={{boxShadow: '0 0 0 2px var(--chakra-colors-gray-400)'}}
      _focusVisible={{boxShadow: '0 0 0 2px var(--chakra-colors-gray-400)'}}
      role="option"
      aria-selected={isSelected}
    >
      <Text fontSize="md" fontWeight="normal">
        {language}
      </Text>
      <HStack spacing={2}>
        {dialectCount > 0 && (
          <Text fontSize="sm" color={dialectColor}>
            {dialectCount} dialect{dialectCount !== 1 ? 's' : ''}
          </Text>
        )}
        {isCommon && (
          <Text
            fontSize="sm"
            bg={commonBadgeBg}
            color={commonBadgeColor}
            px={2}
            py={0.5}
            borderRadius="md"
            fontWeight="normal"
          >
            Common
          </Text>
        )}
        <FavoriteStar
          isFavorite={Boolean(isFavorite)}
          onToggle={onToggleFavorite}
          language={language}
        />
      </HStack>
    </Button>
  );
}
